/**
 * Utilitários de validação canônica de documentos (CPF, CNPJ)
 */

/**
 * Validação matemática oficial de CPF (Cadastro de Pessoas Físicas)
 * Executa o algoritmo de Módulo 11 para os dois dígitos verificadores
 * e rejeita números compostos por dígitos idênticos repetidos.
 *
 * @param cpf string contendo o CPF formatado ou apenas dígitos
 * @returns true se o CPF for matematicamente válido, false caso contrário
 */
export function isValidCPF(cpf: string | null | undefined): boolean {
  if (!cpf) return false;
  const cleanCpf = String(cpf).replace(/\D/g, '');

  if (cleanCpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

  const calcDigit = (slice: string, factor: number): number => {
    let sum = 0;
    for (const char of slice) {
      sum += parseInt(char, 10) * factor--;
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 || remainder === 11 ? 0 : remainder;
  };

  const digit1 = calcDigit(cleanCpf.slice(0, 9), 10);
  const digit2 = calcDigit(cleanCpf.slice(0, 10), 11);

  return (
    digit1 === parseInt(cleanCpf.charAt(9), 10) &&
    digit2 === parseInt(cleanCpf.charAt(10), 10)
  );
}

/**
 * Validação matemática oficial de CNPJ (Cadastro Nacional da Pessoa Jurídica)
 * Executa o cálculo ponderado dos dois dígitos verificadores
 * e rejeita números compostos por dígitos idênticos repetidos.
 *
 * @param cnpj string contendo o CNPJ formatado ou apenas dígitos
 * @returns true se o CNPJ for matematicamente válido, false caso contrário
 */
export function isValidCNPJ(cnpj: string | null | undefined): boolean {
  if (!cnpj) return false;
  const cleanCnpj = String(cnpj).replace(/\D/g, '');

  if (cleanCnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cleanCnpj)) return false;

  const calcDigit = (slice: string, weights: number[]): number => {
    let sum = 0;
    for (let i = 0; i < slice.length; i++) {
      sum += parseInt(slice[i], 10) * weights[i];
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const digit1 = calcDigit(cleanCnpj.slice(0, 12), weights1);
  const digit2 = calcDigit(cleanCnpj.slice(0, 13), weights2);

  return (
    digit1 === parseInt(cleanCnpj.charAt(12), 10) &&
    digit2 === parseInt(cleanCnpj.charAt(13), 10)
  );
}
